import {
    Component,
    Input,
    ViewChild,
    ElementRef,
    AfterViewInit,
    AfterViewChecked,
    EventEmitter,
    Output, OnInit,
    OnDestroy,
    ChangeDetectorRef,
    Renderer2,
    NgZone,
    HostBinding,
    TemplateRef,
    forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DomHandler } from 'primeng/dom';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AutocompleteComponent } from '../Autocomplete/autocomplete.component';
import { AppearCondition, AutocompleteCustomOption } from '../Autocomplete/autocomplete-custom-option.model';
import { MultiSelectDropdownOptionGroup, MultiSelectDropdownOptionModel } from '../../models/dropdown-option.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ActionKeys } from '../../enums/keys.enum';
import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { FocusHelper } from '../../helpers/focus.helper';


/**
 * ### Accessibility Usage
 * + ***ariaLabel*** - use this to provide an accessible label for the search field
 * + ***ariaLabelledBy*** - use this to associate an external label with the search field. Use id of the label as this value
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 */
@Component({
    selector: 'agpr-multi-select-dropdown',
    templateUrl: './multi-select-dropdown.component.html',
    styleUrls: ['./multi-select-dropdown.component.scss'],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0
            })),
            state('visible', style({
                opacity: 1
            })),
            transition('visible => hidden', animate('400ms ease-in')),
            transition('hidden => visible', animate('400ms ease-out'))
        ])
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectDropdownComponent),
            multi: true
        }
    ],
    standalone: false
})
export class MultiSelectDropdownComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy, ControlValueAccessor {
    @Input() id: string;
    @Input() placeholder: string;
    @Input() disabled: boolean = false;
    @Input() required: boolean = false;
    @Input() panelStyleClass: string = '';
    @Input() label: string;
    @Input() ariaLabel: string;
    @Input() ariaLabelledBy: string = '';
    @Input() tooltip: string;
    @Input() tooltipPath: string;
    @Input() hint: string;
    @Input() maxSelected: number;
    @Input() tooltipPosition: string = 'right';
    @Input() displayChips: boolean = true;
    // For static options - disables autocomplete
    @Input() optionsList: MultiSelectDropdownOptionModel[];
    @Input() optionsListGroups: MultiSelectDropdownOptionGroup[];
    @Input() icon: string;
    @Input() alignPanelRight: boolean = false;

    // Required for autocomplete unless optionsTranslator is provided OR options already have label and value properties
    @Input() labelLookupField: string;
    @Input() keyLookupField: string;

    // Optional function to map more complex options to MultiSelectDropdownOptionModel if needed
    // If ommitted, labelLookupKey and valueLookupKey are required
    @Input() optionsConverter: (options: any[]) => MultiSelectDropdownOptionModel[];
    @Input() optionsFetcher: Observable<any[]>;

    @Input() customAutocompleteOptions: AutocompleteCustomOption[];

    public value: any[] = [];
    public optionsToDisplay: MultiSelectDropdownOptionModel[] = [];
    public selectedOptions: MultiSelectDropdownOptionModel[] = [];
    options: MultiSelectDropdownOptionModel[] = [];
    options$: BehaviorSubject<MultiSelectDropdownOptionModel[]> = new BehaviorSubject(null);

    readonly buttonTypes = ButtonTypesEnum;

    @Output() change: EventEmitter<any> = new EventEmitter();

    @Output() blur: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('dropdown') dropdown: ElementRef;
    @ViewChild('container') containerViewChild: ElementRef;
    @ViewChild('panel') panelViewChild: ElementRef;
    @ViewChild('focusInput') focusViewChild: ElementRef;
    @ViewChild('template') checkboxTemplate: TemplateRef<any>;
    @ViewChild('noContent') noContentOption: TemplateRef<any>;
    @ViewChild('chips') chipList: ElementRef;
    @ViewChild(AutocompleteComponent) autocomplete: AutocompleteComponent;
    @ViewChild('itemswrapper')
    set itemsWrapperViewChild(element: ElementRef) {
        if (element) {
            this.itemsWrapper = element.nativeElement;
        }
    };


    @HostBinding('class.ui-inputwrapper-focus') focused: boolean = false;

    public selfClick: boolean;
    public itemClick: boolean;
    public panelVisible: boolean = false;
    public panel: HTMLDivElement;
    public shown: boolean;
    public container: HTMLDivElement;
    public documentClickListener: any;
    public optionsChanged: boolean;
    public itemsWrapper: HTMLDivElement;
    public scrollableParents: any;
    private selectedElement: number = -1;
    private optionElements: any;
    private subscriptions: Subscription[] = [];
    public isAutocomplete: boolean = false;
    public autocompleteSelection: any;
    searchString: string;
    messageSeverityEnum = messageSeverityEnum;
    maxOptionsSelected: boolean = false;
    clearSelectionLabel: string = 'Core.button.clearAll';

    constructor(public el: ElementRef,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public zone: NgZone,
        public localizationService: LocalizationService) {}

    private readonly closePanel = () =>  {
        this.hide();
    };

    private readonly keydownfn = (event) => {
        this.onKeydown(event);
    };

    ngOnInit(): void {
        if (this.optionsList) {
            this.options = this.optionsList;
            this.optionsToDisplay = this.options;
            this.optionsChanged = true;
        }
        if (this.optionsFetcher) {
            this.subscriptions.push(
                this.optionsFetcher.subscribe(options => {
                    if (!!options) {
                        if (this.optionsConverter) {
                            this.options = this.optionsConverter(options);
                        } else {
                            this.options = options.map(item => {
                                return new MultiSelectDropdownOptionModel(
                                    item[this.labelLookupField],
                                    item[this.keyLookupField]
                                );
                            });
                        }
                        this.optionsChanged = true;
                        this.optionsToDisplay = this.options;
                        this.options$.next(this.optionsToDisplay);
                        this.isAutocomplete = true;
                    }
                })
            );
        }
        if (this.label) {
            this.ariaLabelledBy = this.id + '-label';
        }
        if (this.hint) {
            this.ariaLabelledBy = this.ariaLabelledBy.concat(' ', this.id + '-hint');
        }
    }

    ngAfterViewInit() {
        // @TODO: These exist because domHandler needs them
        this.container = <HTMLDivElement>this.containerViewChild.nativeElement;
        this.panel = <HTMLDivElement>this.panelViewChild.nativeElement;
        document.body.appendChild(this.panel);

        if (this.isAutocomplete) {
            requestAnimationFrame(() => {
                const noSuggestionsOption = new AutocompleteCustomOption();
                noSuggestionsOption.appearCondition = AppearCondition.noSuggestions;
                if (this.customAutocompleteOptions) {
                    this.customAutocompleteOptions.push(noSuggestionsOption);
                } else {
                    this.customAutocompleteOptions = [noSuggestionsOption];
                }
            });
        }
    }

    ngAfterViewChecked() {
        if (this.shown) {
            this.onShow();
            this.shown = false;
        }

        if (this.optionsChanged && this.panelVisible) {
            this.optionsChanged = false;

            this.zone.runOutsideAngular(() => {
                setTimeout(() => {
                    this.alignPanel();
                }, 1);
            });
        }
    }

    /**
     * Required by ControlValueAccessor
	 * to identify when the model has changed
     */
    onModelChange: Function = () => { };

    /**
     * Required by ControlValueAccessor
     * to identify when the input has been touched
     */
    onModelTouched: Function = () => { };

    /**
     * Required by ControlValueAccessor
     * to write the value to the view if the value changes occur on the model programmatically
	 */
    writeValue(value: any): void {
        this.selectedOptions = [];
        this.options.forEach(option => {
            option.selected = value?.includes(option.value);
            if (option.selected) {
                this.selectedOptions.push(option);
            }
        });
        this.updateMaxOptionsSelected();

        this.cd.markForCheck();
    }

    onItemClick(option: any) {
        this.itemClick = true;
        if (!option.selected && this.maxOptionsSelected) {
            return;
        } else {
            option.selected = !option.selected;
            this.toggleItem(option);
        }
    }

    toggleItem(option) {
        if (option.selected) {
            this.selectedOptions = [...this.selectedOptions, option];
            this.updateMaxOptionsSelected();
        } else {
            this.removeItem(option, false);
        }
        this.updateValue();
    }

    toggleAutocompleteItem(option) {
        this.itemClick = true;
        this.autocomplete.clearInput();
        const match = this.optionsToDisplay.find(x => option.value === x.value);
        if (!match.selected && this.maxOptionsSelected) {
            return;
        } else {
            match.selected = !match.selected;
            this.toggleItem(match);
        }
    }

    removeItem(option, update: boolean = true) {
        const index = this.selectedOptions.findIndex(selectedOption => selectedOption.value === option.value);
        if (index > -1) {
            this.selectedOptions[index].selected = false;
        }
        this.selectedOptions = this.selectedOptions.filter(selectedOption => selectedOption.selected);
        this.updateMaxOptionsSelected();
        if (update) {
            this.updateValue();
            if (this.selectedOptions.length === 0) {
                this.focusInput();
            } else {
                if (this.displayChips) {
                    FocusHelper.handleFocusAfterChipRemoval(this.chipList.nativeElement, index, 'button');
                }
            }
        }
    }

    updateValue() {
        this.value = [];
        if (this.selectedOptions.length > 0) {
            this.value = this.selectedOptions.map(option => option.value);
        }
        if (this.optionsListGroups) {
            this.value = this.optionsListGroups.reduce((x, y) => {
                const selectedOptionsInGroup = y.options?.filter(option => option.selected) || [];
                return x.concat(selectedOptionsInGroup);
            }, []);
        }
        this.onModelChange(this.value);
    }

    removeAll() {
        this.selectedOptions = [];
        this.updateMaxOptionsSelected();
        this.optionsToDisplay.map(option => option.selected = false);
        this.updateValue();
        this.focusInput();
    }

    updateMaxOptionsSelected() {
        this.maxOptionsSelected = this.selectedOptions.length >= this.maxSelected;
    }

    /**
     * Required by ControlValueAccessor
     * register a callback function to know when the control's value changes in the UI
     */
    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    /**
     * Required by ControlValueAccessor
     * register a callback function to know when the model has been blurred or "touched"
     */
    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onMouseclick(event) {
        if (this.disabled) {
            return;
        }

        this.selfClick = true;

        if (!this.itemClick) {
            if (this.panelVisible) {
                this.hide();
            } else if (this.isAutocomplete) {
                if (!this.autocomplete.autocomplete.overlayVisible) {
                    this.show();
                } else {
                    this.autocomplete.autocomplete.hide();
                }
            } else {
                this.show();
            }
        }
    }

    onShow() {
        this.bindDocumentClickListener();
        this.addEventListeners();

        if ((this.options && this.options.length) || this.optionsListGroups?.length) {
            this.alignPanel();

            const selectedListItem = DomHandler.findSingle(this.itemsWrapper, 'li.agpr-multiSelectDropdown-panel-activeItem');
            if (selectedListItem) {
                selectedListItem.scrollIntoView();
            }
        }
    }

    show() {
        this.panel.style.minWidth = DomHandler.getWidth(this.container) + 'px';
        this.panel.style.zIndex = String(++DomHandler.zindex);
        this.panelVisible = true;
        this.shown = true;
    }

    hide() {
        this.panelVisible = false;
        this.selectedElement = -1;
    }

    alignPanel() {
        DomHandler.absolutePosition(this.panel, this.container);
        if (this.alignPanelRight) {
            this.panel.style.left = (this.panel.offsetLeft - (this.panel.offsetWidth - this.container.offsetWidth)) + 'px';
        }
    }

    onKeydown(event) {
        if (!this.optionsToDisplay || this.optionsToDisplay.length === null) {
            return;
        }
        if (this.isAutocomplete && this.searchString) {
            return;
        }
        switch (event.key) {
            case ActionKeys.DOWN:
                if (!this.panelVisible && this.optionsToDisplay.length > 0) {
                    if (this.isAutocomplete) {
                        this.autocomplete.autocomplete.hide();
                    }
                    this.show();
                    setTimeout(() => this.focusNextOption(), 0); // wait for panel to finish opening to focus first element
                } else if (this.panelVisible) {
                    this.focusNextOption();
                }

                event.preventDefault();

                break;

            case ActionKeys.UP:
                if (this.panelVisible && event.altKey && this.optionsToDisplay.length > 0) {
                    this.hide();
                    if (this.isAutocomplete) {
                        setTimeout(() => this.autocomplete.autocomplete.inputEL.nativeElement.focus());
                    } else {
                        this.focusViewChild.nativeElement.focus();
                    }
                } else if (this.panelVisible) {
                    this.focusPreviousOption();
                }
                event.preventDefault();
                break;

            case ActionKeys.SPACE:
                if (!this.panelVisible  && this.optionsToDisplay.length > 0) {
                    this.show();
                    setTimeout(() => this.focusNextOption(), 0);
                }
                event.preventDefault();
                break;

            case ActionKeys.ENTER:
                if (!this.panelVisible && this.optionsToDisplay.length > 0 && !this.itemClick) {
                    this.show();
                    setTimeout(() => this.focusNextOption(), 0);
                } else if (this.panelVisible) {
                    this.options[this.selectedElement].selected = !this.options[this.selectedElement].selected;
                    this.toggleItem(this.options[this.selectedElement]);
                }
                this.itemClick = false;
                event.preventDefault();
                break;

            case ActionKeys.ESCAPE:
            case ActionKeys.TAB:
                if (this.panelVisible) {
                    this.hide();
                    if (this.isAutocomplete) {
                        setTimeout(() => this.autocomplete.autocomplete.inputEL.nativeElement.focus());
                    } else {
                        this.focusViewChild.nativeElement.focus();
                    }
                    event.preventDefault();
                }

                break;
        }
    }

    focusNextOption() {
        if (this.selectedElement === this.options.length - 1) {
            this.selectedElement = 0;
        } else {
            this.selectedElement++;
        }
        this.optionElements[this.selectedElement].focus();
    }

    focusPreviousOption() {
        if (this.selectedElement === 0) {
            this.selectedElement = this.options.length - 1;
        } else {
            this.selectedElement--;
        }
        this.optionElements[this.selectedElement].focus();
    }

    focusInput() {
        if (this.isAutocomplete) {
            this.autocomplete.autocomplete.inputEL.nativeElement.focus();
        } else {
            requestAnimationFrame(() => {
                this.focusViewChild.nativeElement.focus();
            });
        }
    }

    addEventListeners() {
        this.scrollableParents = DomHandler.getScrollableParents(this.el.nativeElement);

        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.addEventListener('scroll', this.closePanel);
            });
        }
        window.addEventListener('resize', this.closePanel);

        this.optionElements = this.panel.querySelectorAll('li.agpr-multiSelectDropdown-panel-item');
        this.optionElements.forEach(option => option.addEventListener('keydown', this.keydownfn));
    }

    removeEventListeners() {
        if (this.scrollableParents) {
            this.scrollableParents.forEach(element => {
                element.removeEventListener('scroll', this.closePanel);
            });
        }
        window.removeEventListener('resize', this.closePanel);

        if (this.optionElements) {
            this.optionElements.forEach(option => option.removeEventListener('keydown', this.keydownfn));
        }
    }

    /**
     * Closes panel on clicks outside the dropdown.
     */
    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', () => {
                if (!this.selfClick && !this.itemClick) {
                    this.panelVisible = false;
                    this.unbindDocumentClickListener();
                }

                this.selfClick = false;
                this.itemClick = false;
                this.cd.markForCheck();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    toggleOptionsListGroup(group: MultiSelectDropdownOptionGroup): void {
        group.expanded = !group.expanded;
        this.optionsListGroups.forEach(x => {
            if (x !== group && group.expanded) {
                x.expanded = false;
            }
        });
    }

    ngOnDestroy() {
        this.unbindDocumentClickListener();
        this.removeEventListeners();

        if (this.panel) {
            this.panel.parentNode.removeChild(this.panel);
        }

        if (this.subscriptions) {
            this.subscriptions.forEach(sub => sub.unsubscribe());
        }
    }
}
